export const SECURITY_TRUST_PATH = '/iso270001';
export const SECURITY_TRUST_ROBOTS = 'index,follow,max-image-preview:large';
export const SECURITY_TRUST_STRUCTURED_DATA_ID = 'security-trust-structured-data';
export const SECURITY_TRUST_SEO_HEAD_START = '<!-- PALEN_SEO_HEAD_START -->';
export const SECURITY_TRUST_SEO_HEAD_END = '<!-- PALEN_SEO_HEAD_END -->';

const SECURITY_TRUST_SEO_BY_LANGUAGE = {
  fr: {
    title: 'Standards de sécurité et démarche ISO 27001 | Palen',
    description:
      'Palen applique des mesures de sécurité guidées par ISO/IEC 27001:2022 à la conception, au développement et à l’exploitation du service.',
    url: 'https://palen-adr.eu/iso270001',
    language: 'fr',
    locale: 'fr_FR',
    imageAlt: 'Démarche de sécurité de l’information Palen',
  },
  en: {
    title: 'Security standards and ISO 27001 approach | Palen',
    description:
      'Palen applies security measures guided by ISO/IEC 27001:2022 throughout the design, development and operation of the service.',
    url: 'https://palen-adr.eu/en/iso270001',
    language: 'en',
    locale: 'en_US',
    imageAlt: 'Palen information security approach',
  },
} as const;

const SHARED_SECURITY_TRUST_SEO = {
  robots: SECURITY_TRUST_ROBOTS,
  dateModified: '2026-09-03',
  image: 'https://palen-adr.eu/assets/logo/palen_social_card_20260710.png',
  imageWidth: '1200',
  imageHeight: '627',
} as const;

export type SecurityTrustSeoLanguage = keyof typeof SECURITY_TRUST_SEO_BY_LANGUAGE;

export function getSecurityTrustSeo(language: SecurityTrustSeoLanguage = 'fr') {
  return { ...SHARED_SECURITY_TRUST_SEO, ...SECURITY_TRUST_SEO_BY_LANGUAGE[language] } as const;
}

export const SECURITY_TRUST_SEO = getSecurityTrustSeo('fr');

export function buildSecurityTrustStructuredData(
  language: SecurityTrustSeoLanguage = 'fr',
) {
  const seo = getSecurityTrustSeo(language);
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${seo.url}#webpage`,
    name: seo.title,
    description: seo.description,
    url: seo.url,
    dateModified: seo.dateModified,
    inLanguage: seo.language,
    isPartOf: {
      '@id': language === 'en'
        ? 'https://palen-adr.eu/en/#website'
        : 'https://palen-adr.eu/#website',
    },
    about: language === 'en'
      ? ['Information security', 'Security by design', 'ISO/IEC 27001:2022']
      : ['Sécurité de l’information', 'Sécurité dès la conception', 'ISO/IEC 27001:2022'],
    publisher: {
      '@type': 'Organization',
      '@id': 'https://palen-adr.eu/#organization',
      name: 'Palen',
      url: 'https://palen-adr.eu/',
    },
  } as const;
}

export function buildSecurityTrustSeoHeadMarkup(
  language: SecurityTrustSeoLanguage = 'fr',
): string {
  const seo = getSecurityTrustSeo(language);

  return `
    ${SECURITY_TRUST_SEO_HEAD_START}
    <title>${seo.title}</title>
    <meta name="description" content="${seo.description}" />
    <meta name="robots" content="${seo.robots}" />
    <link rel="canonical" href="${seo.url}" />
    <link rel="alternate" hreflang="fr" href="${SECURITY_TRUST_SEO_BY_LANGUAGE.fr.url}" />
    <link rel="alternate" hreflang="en" href="${SECURITY_TRUST_SEO_BY_LANGUAGE.en.url}" />
    <link rel="alternate" hreflang="x-default" href="${SECURITY_TRUST_SEO_BY_LANGUAGE.fr.url}" />
    <link rel="preload" as="image" href="/assets/logo/palen_platform-180.webp?v=20260807" type="image/webp" fetchpriority="high" />
    <meta property="og:type" content="article" />
    <meta property="og:site_name" content="Palen" />
    <meta property="og:title" content="${seo.title}" />
    <meta property="og:description" content="${seo.description}" />
    <meta property="og:url" content="${seo.url}" />
    <meta property="og:image" content="${seo.image}" />
    <meta property="og:image:width" content="${seo.imageWidth}" />
    <meta property="og:image:height" content="${seo.imageHeight}" />
    <meta property="og:image:alt" content="${seo.imageAlt}" />
    <meta property="og:locale" content="${seo.locale}" />
    <meta property="article:modified_time" content="${seo.dateModified}" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content="${seo.title}" />
    <meta name="twitter:description" content="${seo.description}" />
    <meta name="twitter:image" content="${seo.image}" />
    <meta name="twitter:image:alt" content="${seo.imageAlt}" />
    <script id="${SECURITY_TRUST_STRUCTURED_DATA_ID}" type="application/ld+json">${serializeStructuredData(buildSecurityTrustStructuredData(language))}</script>
    ${SECURITY_TRUST_SEO_HEAD_END}`;
}

function serializeStructuredData(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}
